﻿<template>
  <!--
    A fan, drawn in the appliances' glass idiom (`switch/SwitchAnimation.vue`):
    one inline SVG on a 48-grid in `currentColor`, the casing left
    semi-transparent so what is inside it stays visible, and one dark outline
    holding the shape together.

    Off, the drawing is complete but still; running, the blade groups turn at the
    speed the host names (`--fan-duration`) and the blades take the denser
    "moving part" tone. Nothing here is coloured: a fan has no medium to show.

    Every kind shares that language so the eight read as one family: `ceiling` the
    ceiling fan from below with its blade-sweep ring, `pedestal` a head on a tall
    pole and a disc base, `table` a bigger head on a stub, `wall` the same head on
    a bracket beside its wall, `tower` a column with its outlet grille and the air
    rising out of it, `extractor` a bathroom fan's square plate and horizontal
    grille, `duct` an inline fan inside its duct with the air leaving to the right,
    and `erv` a heat-recovery unit with its four spigots and two counter-turning fans.
  -->
  <svg
    class="fan-anim"
    :class="[`fan-anim--${kind}`, { 'fan-anim--active': active }]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false">
    <!-- ── Ceiling: seen from below, the blades bare inside their sweep ─────── -->
    <g v-if="kind === 'ceiling'">
      <circle class="fan-anim__sweep" cx="24" cy="25" r="19.2" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '24px 25px' }">
        <path
          class="fan-anim__blade"
          d="M24,22.2 C21.4,17.4 18.4,11.4 20.4,8.2 C21.4,6.4 26.2,6.2 27.2,8.2 C28.8,11.6 26.4,17.6 24,22.2 Z" />
        <path
          class="fan-anim__blade"
          transform="rotate(120 24 25)"
          d="M24,22.2 C21.4,17.4 18.4,11.4 20.4,8.2 C21.4,6.4 26.2,6.2 27.2,8.2 C28.8,11.6 26.4,17.6 24,22.2 Z" />
        <path
          class="fan-anim__blade"
          transform="rotate(240 24 25)"
          d="M24,22.2 C21.4,17.4 18.4,11.4 20.4,8.2 C21.4,6.4 26.2,6.2 27.2,8.2 C28.8,11.6 26.4,17.6 24,22.2 Z" />
      </g>
      <circle class="fan-anim__hub" cx="24" cy="25" r="3.6" />
      <circle class="fan-anim__wire" cx="24" cy="25" r="1.3" />
    </g>

    <!-- ── Pedestal: a tripod stand, a tall pole, the head on top ───────────── -->
    <g v-else-if="kind === 'pedestal'">
      <path class="fan-anim__leg" d="M24,36.4 L15.6,45.4" />
      <path class="fan-anim__leg" d="M24,36.4 L32.4,45.4" />
      <path class="fan-anim__leg" d="M24,36.4 V45.8" />
      <rect class="fan-anim__part" x="21.8" y="34.8" width="4.4" height="3.4" rx="1.7" />
      <rect class="fan-anim__part" x="22.9" y="25.6" width="2.2" height="10.4" rx="1.1" />
      <rect class="fan-anim__part" x="21.6" y="31.6" width="4.8" height="2.4" rx="1.2" />
      <path class="fan-anim__part" d="M21.6,7.2 H26.4 L28,10.4 H20 Z" />
      <circle class="fan-anim__glass" cx="24" cy="16.8" r="9.4" />
      <circle class="fan-anim__wire" cx="24" cy="16.8" r="6.4" />
      <circle class="fan-anim__wire" cx="24" cy="16.8" r="3.4" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '24px 16.8px' }">
        <path
          class="fan-anim__blade"
          transform="translate(24 16.8)"
          d="M0,-1.5 C-1.7,-3.3 -3,-5.5 -1.8,-7.1 -1,-8 1,-7.9 1.5,-6.9 2.1,-5.3 1.3,-3.2 0,-1.5 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 16.8) rotate(120)"
          d="M0,-1.5 C-1.7,-3.3 -3,-5.5 -1.8,-7.1 -1,-8 1,-7.9 1.5,-6.9 2.1,-5.3 1.3,-3.2 0,-1.5 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 16.8) rotate(240)"
          d="M0,-1.5 C-1.7,-3.3 -3,-5.5 -1.8,-7.1 -1,-8 1,-7.9 1.5,-6.9 2.1,-5.3 1.3,-3.2 0,-1.5 Z" />
      </g>
      <circle class="fan-anim__hub" cx="24" cy="16.8" r="2.2" />
    </g>

    <!-- ── Table: the same head, larger, on a stub and a small oval base ────── -->
    <g v-else-if="kind === 'table'">
      <ellipse class="fan-anim__shell" cx="24" cy="43.6" rx="8.4" ry="2.8" />
      <rect class="fan-anim__part" x="22.4" y="33.4" width="3.2" height="9.4" rx="1.6" />
      <rect class="fan-anim__part" x="21.2" y="31.4" width="5.6" height="2.6" rx="1.3" />
      <circle class="fan-anim__glass" cx="24" cy="22.6" r="12" />
      <circle class="fan-anim__wire" cx="24" cy="22.6" r="8.4" />
      <circle class="fan-anim__wire" cx="24" cy="22.6" r="4.4" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '24px 22.6px' }">
        <path
          class="fan-anim__blade"
          transform="translate(24 22.6)"
          d="M0,-2.2 C-2.2,-4.6 -3.9,-7.4 -2.3,-9.6 -1.2,-10.8 1.4,-10.6 2,-9.2 2.8,-7 1.7,-4.3 0,-2.2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 22.6) rotate(120)"
          d="M0,-2.2 C-2.2,-4.6 -3.9,-7.4 -2.3,-9.6 -1.2,-10.8 1.4,-10.6 2,-9.2 2.8,-7 1.7,-4.3 0,-2.2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 22.6) rotate(240)"
          d="M0,-2.2 C-2.2,-4.6 -3.9,-7.4 -2.3,-9.6 -1.2,-10.8 1.4,-10.6 2,-9.2 2.8,-7 1.7,-4.3 0,-2.2 Z" />
      </g>
      <circle class="fan-anim__hub" cx="24" cy="22.6" r="2.8" />
    </g>

    <!-- ── Wall: the wall on the left, the bracket, the head on its end ─────── -->
    <g v-else-if="kind === 'wall'">
      <rect class="fan-anim__shell" x="4.2" y="6.4" width="4.8" height="35.2" rx="2.4" />
      <path class="fan-anim__wire" d="M4.6,13.6 L8.6,18" />
      <path class="fan-anim__wire" d="M4.6,23 L8.6,27.4" />
      <path class="fan-anim__wire" d="M4.6,32.4 L8.6,36.8" />
      <rect class="fan-anim__part" x="9" y="22.4" width="7.6" height="3.2" rx="1.6" />
      <rect class="fan-anim__part" x="15.4" y="20.6" width="5.4" height="6.8" rx="1.8" />
      <circle class="fan-anim__glass" cx="30.4" cy="24" r="11.2" />
      <circle class="fan-anim__wire" cx="30.4" cy="24" r="7.7" />
      <circle class="fan-anim__wire" cx="30.4" cy="24" r="4" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '30.4px 24px' }">
        <path
          class="fan-anim__blade"
          transform="translate(30.4 24)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(30.4 24) rotate(120)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(30.4 24) rotate(240)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
      </g>
      <circle class="fan-anim__hub" cx="30.4" cy="24" r="2.6" />
    </g>

    <!-- ── Tower: a column, its outlet grille, and the air rising through it ── -->
    <g v-else-if="kind === 'tower'">
      <ellipse class="fan-anim__part" cx="24" cy="42.6" rx="10.8" ry="2.8" />
      <rect class="fan-anim__shell" x="13.4" y="5.4" width="21.2" height="35.4" rx="6" />
      <rect class="fan-anim__glass" x="18.2" y="10.4" width="11.6" height="24.8" rx="5.8" />
      <path class="fan-anim__slat" d="M19.2,14 H28.8" />
      <path class="fan-anim__slat" d="M19.2,17.6 H28.8" />
      <path class="fan-anim__slat" d="M19.2,21.2 H28.8" />
      <path class="fan-anim__slat" d="M19.2,24.8 H28.8" />
      <path class="fan-anim__slat" d="M19.2,28.4 H28.8" />
      <path class="fan-anim__slat" d="M19.2,32 H28.8" />
      <path class="fan-anim__wisp" d="M21.4,32.6 L24,30.2 L26.6,32.6" />
      <path class="fan-anim__wisp fan-anim__wisp--2" d="M21.4,26.4 L24,24 L26.6,26.4" />
      <circle class="fan-anim__part" cx="24" cy="8.2" r="1.3" />
    </g>

    <!-- ── Extractor: the bathroom's square plate and its horizontal grille ── -->
    <g v-else-if="kind === 'extractor'">
      <rect class="fan-anim__shell" x="6.6" y="6.6" width="34.8" height="34.8" rx="3.6" />
      <circle class="fan-anim__glass" cx="24" cy="24" r="12.8" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '24px 24px' }">
        <path
          class="fan-anim__blade"
          transform="translate(24 24)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 24) rotate(120)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
        <path
          class="fan-anim__blade"
          transform="translate(24 24) rotate(240)"
          d="M0,-2 C-2,-4.2 -3.5,-6.8 -2.1,-8.8 -1.1,-9.9 1.3,-9.7 1.8,-8.4 2.6,-6.4 1.6,-3.9 0,-2 Z" />
      </g>
      <circle class="fan-anim__hub" cx="24" cy="24" r="2.6" />
      <!-- The grille: five slats across the opening, their ends on the circle
           (owner, 03.11.2026: no screws, no inner rings, a horizontal grille). -->
      <path class="fan-anim__slat" d="M14.01,16 H33.99" />
      <path class="fan-anim__slat" d="M11.84,20 H36.16" />
      <path class="fan-anim__slat" d="M11.2,24 H36.8" />
      <path class="fan-anim__slat" d="M11.84,28 H36.16" />
      <path class="fan-anim__slat" d="M14.01,32 H33.99" />
    </g>

    <!-- ── Duct: an inline fan in its duct, the impeller visible through it ── -->
    <g v-else-if="kind === 'duct'">
      <rect class="fan-anim__part" x="3.6" y="15.4" width="5.2" height="17.2" rx="1.8" />
      <rect class="fan-anim__glass" x="8.2" y="17.6" width="26.4" height="12.8" rx="3.2" />
      <rect class="fan-anim__part" x="34.4" y="15.4" width="5.2" height="17.2" rx="1.8" />
      <rect class="fan-anim__part" x="20.4" y="12.2" width="7.2" height="6" rx="2.2" />
      <rect class="fan-anim__part" x="22.4" y="9.2" width="3.2" height="3.4" rx="1" />
      <circle class="fan-anim__wire" cx="24" cy="24" r="6.2" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '24px 24px' }">
        <path class="fan-anim__blade-line" d="M0.9,-4.6 C2.9,-3.8 3.2,-1.1 1.6,0.3" transform="translate(24 24)" />
        <path
          class="fan-anim__blade-line"
          d="M0.9,-4.6 C2.9,-3.8 3.2,-1.1 1.6,0.3"
          transform="translate(24 24) rotate(120)" />
        <path
          class="fan-anim__blade-line"
          d="M0.9,-4.6 C2.9,-3.8 3.2,-1.1 1.6,0.3"
          transform="translate(24 24) rotate(240)" />
      </g>
      <circle class="fan-anim__hub" cx="24" cy="24" r="1.8" />
      <path class="fan-anim__wisp fan-anim__wisp--right" d="M41.4,20.4 L44,24 L41.4,27.6" />
      <path class="fan-anim__wisp fan-anim__wisp--right fan-anim__wisp--2" d="M41.4,20.4 L44,24 L41.4,27.6" />
    </g>

    <!-- ── ERV: the heat-recovery unit — four spigots, two counter-turning fans ── -->
    <g v-else>
      <rect
        v-for="x in [11.5, 18.1, 24.7, 31.3]"
        :key="x"
        class="fan-anim__part"
        :x="x"
        y="4.6"
        width="5.2"
        height="8.4"
        rx="1.4" />
      <rect class="fan-anim__shell" x="8.4" y="11.8" width="31.2" height="23.6" rx="3.6" />
      <path class="fan-anim__wire" d="M24,15.2 V32.2" />
      <path class="fan-anim__wire" d="M8.4,28.6 H39.6" />
      <circle class="fan-anim__glass" cx="16.4" cy="21.8" r="5.4" />
      <g class="fan-anim__blades" :style="{ transformOrigin: '16.4px 21.8px' }">
        <path class="fan-anim__blade-line" transform="translate(16.4 21.8)" d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
        <path
          class="fan-anim__blade-line"
          transform="translate(16.4 21.8) rotate(120)"
          d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
        <path
          class="fan-anim__blade-line"
          transform="translate(16.4 21.8) rotate(240)"
          d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
      </g>
      <circle class="fan-anim__hub" cx="16.4" cy="21.8" r="1.5" />
      <circle class="fan-anim__glass" cx="31.6" cy="21.8" r="5.4" />
      <g class="fan-anim__blades fan-anim__blades--reverse" :style="{ transformOrigin: '31.6px 21.8px' }">
        <path class="fan-anim__blade-line" transform="translate(31.6 21.8)" d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
        <path
          class="fan-anim__blade-line"
          transform="translate(31.6 21.8) rotate(120)"
          d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
        <path
          class="fan-anim__blade-line"
          transform="translate(31.6 21.8) rotate(240)"
          d="M0.7,-3.6 C2.3,-3 2.5,-0.9 1.3,0.2" />
      </g>
      <circle class="fan-anim__hub" cx="31.6" cy="21.8" r="1.5" />
    </g>
  </svg>
</template>

<script setup lang="ts">
import type { FanKind } from '../deviceWidgetTypes';

withDefaults(
  defineProps<{
    /** Which fan this is — the mounting the tile has to show. */
    kind?: FanKind;
    /** The fan is running — the only thing the movement is keyed off. */
    active?: boolean;
  }>(),
  { kind: 'ceiling', active: false },
);
</script>

<style scoped>
.fan-anim {
  display: block;
  width: 100%;
  height: 100%;
  /* The glass palette: a shell you see through, a denser part, a look-through
     pane, the tone the moving part takes while it runs, and one dark outline. */
  --fan-shell: color-mix(in srgb, currentColor 30%, transparent);
  --fan-part: color-mix(in srgb, currentColor 46%, transparent);
  --fan-glass: color-mix(in srgb, currentColor 10%, transparent);
  --fan-move: color-mix(in srgb, currentColor 64%, transparent);
  --fan-edge: color-mix(in srgb, currentColor 78%, rgb(0 0 0));
  --fan-wire: color-mix(in srgb, currentColor 52%, transparent);
}

.fan-anim__shell {
  fill: var(--fan-shell);
  stroke: var(--fan-edge);
  stroke-width: 1.2;
}

.fan-anim__part {
  fill: var(--fan-part);
  stroke: var(--fan-edge);
  stroke-width: 1.1;
}

.fan-anim__glass {
  fill: var(--fan-glass);
  stroke: var(--fan-edge);
  stroke-width: 1.1;
}

/* The guard's wires, the seams and the grille's slats. */
.fan-anim__wire,
.fan-anim__slat {
  fill: none;
  stroke: var(--fan-wire);
  stroke-width: 0.9;
  stroke-linecap: round;
}

/* The circle a ceiling fan's blades sweep — the empty arc, not an outline. */
.fan-anim__sweep {
  fill: none;
  stroke: var(--fan-wire);
  stroke-width: 0.9;
  stroke-dasharray: 1.6 2.8;
  stroke-linecap: round;
}

.fan-anim__blade {
  fill: var(--fan-part);
  stroke: var(--fan-edge);
  stroke-width: 0.8;
  stroke-linejoin: round;
}

/* An impeller seen through its casing: no outline, or it closes the duct. */
.fan-anim__blade-line {
  fill: none;
  stroke: var(--fan-move);
  stroke-width: 1.7;
  stroke-linecap: round;
}

.fan-anim__hub {
  fill: var(--fan-part);
  stroke: var(--fan-edge);
  stroke-width: 0.9;
}

/* A stand's legs — the drawn line, not the fill, is the leg. */
.fan-anim__leg {
  fill: none;
  stroke: var(--fan-edge);
  stroke-width: 1.8;
  stroke-linecap: round;
}

/* ── Airflow: the wisps the running fan pushes out of its grille ─────────── */
.fan-anim__wisp {
  fill: none;
  stroke: var(--fan-move);
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0;
}

/* ── Running: the blades turn, the moving parts darken, the air moves ────── */
.fan-anim--active .fan-anim__blade {
  fill: var(--fan-move);
}

.fan-anim--active .fan-anim__blades {
  animation: fan-anim-spin var(--fan-duration, 3.2s) linear infinite;
}

/* A heat-recovery unit has two fans and they turn against each other. */
.fan-anim--active .fan-anim__blades--reverse {
  animation-direction: reverse;
}

.fan-anim--active .fan-anim__wisp {
  animation: fan-anim-wisp 1.8s ease-in-out infinite;
}

.fan-anim--active .fan-anim__wisp--2 {
  animation-delay: 0.6s;
}

.fan-anim--active .fan-anim__wisp--right {
  animation-name: fan-anim-draft;
}

@keyframes fan-anim-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fan-anim-wisp {
  0% {
    transform: translateY(2.4px);
    opacity: 0;
  }
  35% {
    opacity: 0.95;
  }
  100% {
    transform: translateY(-3.2px);
    opacity: 0;
  }
}

@keyframes fan-anim-draft {
  0% {
    transform: translateX(-2.4px);
    opacity: 0;
  }
  35% {
    opacity: 0.95;
  }
  100% {
    transform: translateX(3.2px);
    opacity: 0;
  }
}

/* Reduced motion: the drawing stays, the movement stops. */
@media (prefers-reduced-motion: reduce) {
  .fan-anim--active .fan-anim__blades,
  .fan-anim--active .fan-anim__wisp {
    animation: none !important;
  }

  .fan-anim--active .fan-anim__wisp {
    opacity: 0.6;
  }
}
</style>
